<template>
  <div>
    <SignupTemplate
      :show-back-button="true"
      :slide-animation="slideAnimationActive"
      :tablet-query="true"
      :gradient-background="gradientBackgroundActive"
      :primary="templateBackground"
      :over-primary="templateBackground"
      data-testid="signup-desktop"
      @on-back-click="onBackClick"
    >
      <template #header>
        <div
          :class="{
            signup__infos: true,
            specialist: userHaveAdvisor
          }"
        >
          <div v-if="partnerTheme" class="signup__infos-partner">
            <img
              v-if="partnerTheme.logo"
              :src="partnerTheme.logo"
              :alt="partnerTheme.brandName"
            />
            <Heading v-else :level="1" as="h1">
              {{ partnerTheme.brandName }}
            </Heading>
          </div>
          <div
            v-else-if="!partnerThemeLoading"
            :class="{
              'signup__infos-container': true,
              specialist: userHaveAdvisor
            }"
          >
            <BrandImage
              class="signup__infos-logo"
              :image="EBrandImageLogos.WARREN_SECONDARY"
              :schema="EBrandImageSchemas.POSITIVE"
              :fixed-schema-color="true"
              width="120"
            />
            <div class="signup__infos-heading">
              <Heading :level="1">
                Investir toma tempo, mas não precisa ser o seu
              </Heading>
              <BaseText size="p4" :color="NebraskaColors.elementAlpha">
                Acesse a forma mais eficiente de investir. Para seu patrimônio e
                para sua vida.
              </BaseText>
            </div>
            <FadeTransition>
              <div v-if="userHaveAdvisor" class="signup__infos-specialist">
                <div>
                  <BaseText size="p4" :color="NebraskaColors.elementAlpha">
                    Seu especialista
                  </BaseText>
                  <BaseText size="p3" :color="NebraskaColors.staticWhite">
                    {{ advisor.name }}
                  </BaseText>
                </div>
                <img
                  v-if="advisor.image && imageVisible"
                  :src="advisor.image"
                  :alt="advisor.name"
                  @error="imageVisible = false"
                />
              </div>
            </FadeTransition>
          </div>
        </div>
      </template>
      <template #default>
        <div class="signup__form">
          <div class="signup__form-container">
            <FadeSlideTransition>
              <component :is="componentStep" />
            </FadeSlideTransition>
          </div>
        </div>
      </template>
    </SignupTemplate>
  </div>
</template>
<script setup lang="ts">
import {
  BaseText,
  BrandImage,
  EBrandImageLogos,
  EBrandImageSchemas,
  Heading,
  SignupTemplate
} from '@warrenbrasil/nebraska-web';
import {
  ref,
  provide,
  computed,
  onBeforeMount,
  onMounted,
  onUnmounted
} from 'vue';
import { useRouter, useRoute } from 'vue-router/composables';
import { NebraskaColors } from '@warrenbrasil/nebraska-tokens-web';
import { Advisor, SignUpFormSteps } from '@/modules/onboarding/signup/types';
import { SignUpFormStepComponents } from '@/modules/onboarding/signup/helpers/step-component';
import FadeSlideTransition from '@/modules/common/components/FadeSlideTransition/FadeSlideTransition.vue';
import { FadeTransition } from '@/modules/common/components/FadeTransition';
import { getAdvisor } from '@/modules/onboarding/signup/services/api';
import { getThemeByParam } from '@/modules/common/services/http/account';
import {
  ICustomTheme,
  StorageKeys
} from '@/modules/common/services/local-storage';
import { setNebraskaScheme } from '@/modules/common/services/theme';
import { trackPageView } from '@/modules/onboarding/signup/helpers/track-events';

const router = useRouter();
const route = useRoute();
const formStep = ref<SignUpFormSteps>(SignUpFormSteps.Initial);
const userHaveAdvisor = ref<boolean>(false);
const isAdvisorModalOpen = ref<boolean>(false);
const firstName = ref<string>('');
const email = ref<string>('');
const imageVisible = ref(true);
const advisor = ref<Advisor>({
  id: '',
  name: '',
  image: '',
  email: ''
});
const advisorLoading = ref<boolean>(false);
const partnerTheme = ref<ICustomTheme | null>(null);
const partnerThemeLoading = ref<boolean>(true);

function onBackClick() {
  if (isAdvisorModalOpen.value) {
    isAdvisorModalOpen.value = false;
    return;
  }

  if (formStep.value === SignUpFormSteps.Password) {
    formStep.value = SignUpFormSteps.Initial;
    return;
  }

  router.push({ name: 'signin' });
}

const slideAnimationActive = computed(() => {
  return window.innerWidth > 1024;
});

const templateBackground = computed(() => {
  return !partnerTheme.value ? NebraskaColors.backgroundSecondary : undefined;
});

const gradientBackgroundActive = computed(() => {
  return !partnerTheme.value && !partnerThemeLoading.value;
});

const componentStep = computed(() => {
  return SignUpFormStepComponents[formStep.value];
});

function changeStep(step: SignUpFormSteps) {
  formStep.value = step;
}

const windowWidth = ref(window.innerWidth);
const updateWidth = () => {
  windowWidth.value = window.innerWidth;
};

const isMobile = computed(() => windowWidth.value < 768);

onMounted(() => {
  window.addEventListener('resize', updateWidth);
  document.body.classList.add('signup-page-active');
});
onUnmounted(() => {
  window.removeEventListener('resize', updateWidth);
  document.body.classList.remove('signup-page-active');
});

async function checkPartnerQueryParam() {
  partnerThemeLoading.value = true;
  advisorLoading.value = true;
  if (route.query.advisor) {
    const advisorEmail = route.query.advisor as string;

    try {
      const advisorResponse = await getAdvisor(advisorEmail);
      advisor.value = { ...advisorResponse };
      userHaveAdvisor.value = true;
    } catch {
      advisor.value = { id: '', name: '', image: '', email: '' };
    }

    try {
      const themeResponse = await getThemeByParam('email', advisorEmail);
      if (themeResponse.type !== 'WarrenManaged') {
        partnerTheme.value = themeResponse;
        localStorage.setItem(StorageKeys.Theme, JSON.stringify(themeResponse));
        setNebraskaScheme(themeResponse);
        localStorage.setItem(StorageKeys.WhitelabelTheme, 'whitelabel');
      }
    } catch {
      partnerTheme.value = null;
    }
  }
  advisorLoading.value = false;
  partnerThemeLoading.value = false;
}

onBeforeMount(async () => {
  checkPartnerQueryParam();
  trackPageView();
});

provide('isMobile', isMobile);
provide('changeStep', changeStep);
provide('advisor', advisor);
provide('advisorLoading', advisorLoading);
provide('userHaveAdvisor', userHaveAdvisor);
provide('firstName', firstName);
provide('partnerTheme', partnerTheme);
provide('partnerThemeLoading', partnerThemeLoading);
provide('email', email);
provide('isAdvisorModalOpen', isAdvisorModalOpen);
provide('updateIsAdvisorModalOpen', (value: boolean) => {
  isAdvisorModalOpen.value = value;
});
provide('updateUserHaveAdvisor', (value: boolean) => {
  userHaveAdvisor.value = value;
});
provide('updateAdvisor', (value: Advisor) => {
  advisor.value = value;
});
provide('updateAdvisorLoading', (value: boolean) => {
  advisorLoading.value = value;
});
provide('updateFirstName', (value: string) => {
  firstName.value = value;
});
provide('updateEmail', (value: string) => {
  email.value = value;
});
</script>
<style lang="less" scoped>
:deep(.signin) {
  overflow: hidden;
}
:not(.signin--gradient) > :deep(.signin-template) {
  background: @theme-primary;
}
:deep(.signin-container--left) {
  transition: none;
}
:deep(.signin-container) {
  transition: none;
  justify-content: center;
}

.signup__infos {
  color: @static-white;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  max-height: 654px;
  background-size: cover !important;
  width: 100%;

  .heading {
    color: @static-white !important;
    max-width: 360px;
    margin-top: @size-spacing-x900;
    margin-bottom: @size-spacing-x400;
    text-align: left;
  }

  &-partner {
    display: flex;
    align-items: center;
    justify-content: center;

    img {
      width: 50%;
    }
  }

  &__infos,
  &__form {
    height: 50%;
  }

  &-container {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    margin-top: @size-spacing-x600;
    max-width: 468px;
    width: 100%;
  }

  &-goback {
    position: absolute;
    top: @size-spacing-x300;
    left: @size-spacing-x400;
  }

  &-specialist {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    margin-top: @size-spacing-x1000;

    div {
      display: flex;
      flex-direction: column;
      gap: @size-spacing-x150;
    }

    img {
      width: @size-spacing-x800;
      height: @size-spacing-x800;
      border-radius: @size-radius-max;
    }
  }
}

.signup__form {
  display: flex;
  justify-content: center;
  align-items: center;

  &-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    max-width: 468px;
    width: 100%;
    gap: @size-spacing-x150;
  }
}

.-alert {
  width: 100%;
  position: absolute;
  top: 0;
}

:deep(.button-icon) {
  color: @element-over-image !important;
}

@media screen and (min-width: 1025px) {
  .signup__infos {
    max-height: none;
    justify-content: center;

    &.specialist {
      justify-content: center;
    }

    &-container {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      justify-content: unset;
      margin-top: 0;
      max-width: 468px;
      width: fit-content;
    }

    &-loader {
      height: 58px;
      width: 360px;
      border-radius: @size-radius-x100;
      margin-top: @size-spacing-x1000;
    }

    &-goback {
      position: absolute;
      top: @size-spacing-x300;
      left: @size-spacing-x400;
    }

    &-specialist {
      max-width: 360px;
    }
  }

  .signup__form {
    overflow: hidden;
  }

  .signup__infos,
  .signup__form {
    height: 100%;
    width: 100%;
  }

  h1,
  p {
    max-width: 360px;
  }
}

@media screen and (max-height: 1024px) and (max-width: 1024px) {
  :deep(.signin) {
    overflow: visible;
  }
  height: fit-content;

  .signup {
    &--partner {
      animation: none;
      background: @background-secondary;
    }

    &__infos {
      max-height: none;
      justify-content: center;
      animation: fade-in 1s linear;

      &.specialist {
        justify-content: space-between;
      }

      &-container {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        justify-content: unset;
        margin-top: 0;
        height: fit-content;
        width: fit-content;
        max-width: 468px;
        min-height: 438px;
      }

      &-specialist {
        max-width: 360px;
      }
    }

    &__form {
      overflow: hidden;
    }

    &__infos,
    &__form {
      padding: 80px;
      height: 100%;
    }

    h1,
    p {
      max-width: 384px;
    }
  }
}

@media screen and (max-width: 768px) {
  .signup {
    &--partner {
      animation: none;
      height: 201px;
      justify-content: center;
      background: @background-secondary;
    }

    &__infos {
      height: 201px !important;
      justify-content: center;
      animation: fade-in 1s linear;

      &.specialist {
        justify-content: center;
        height: 201px !important;
      }

      &-heading {
        display: none;
      }

      &-specialist {
        max-width: 360px;
        margin-top: @size-spacing-x300 !important;
      }

      &-container {
        min-height: 64px;
        align-items: center;
        justify-content: center;
        width: 100%;
        height: 201px;

        &.specialist {
          min-height: 128px;
          width: 100%;
          max-width: none;
        }
      }
    }

    &__form {
      width: 100%;
      overflow: hidden;
      align-items: self-start;

      &-container {
        max-width: unset;
      }
    }

    &__infos,
    &__form {
      padding: @size-spacing-x500;
      height: 100%;
    }

    h1,
    p {
      max-width: 384px;
    }
  }

  :deep(.signin-template) {
    height: auto;
  }
}

.signup__infos,
.signup__form {
  height: 100%;
  &-specialist,
  .heading {
    margin-top: @size-spacing-x900;
  }
}
</style>
<style>
.signup-page-active .grecaptcha-badge {
  @media screen and (max-width: 768px) {
    z-index: 6;
    top: 8px;
    visibility: visible;
  }
}
</style>
